<template>
  <div v-if="fields.scheduled"
       :class="[$setClasses, !meetingsSlotsLoading ? 'u-display-flex' : '']">
    <appointment-booking
      v-if="!fields.scheduled.loading"
      class="u-flex-1"
      v-model="model"
      v-model:date-picker-date="datePickerDate"
      :calendar-options="$tm('meeting.calendarOptions')"
      :class="$vClass('booking')"
      :disabled-slot="disabledSlot"
      :loading="meetingsSlotsLoading"
      :meetings-by-days="meetingsSlots"
      @datePickerDateChange="dateChange($event.date)"
      @meetingCheckAndUncheck="$event && meetingCheckAndUncheck($event, lock)"
    >
      <template #meetingSlot="{ meetingSlot }">
        <nxt-core-popover
          :popover="{ placement: 'bottom', trigger: 'hover', openDelay: 600 }"
          :popper-class="`custom ${$vClass('popover')} u-padding-0`"
        >
          <template #reference><div></div></template>
          <template #default>
            <ul v-if="meetingSlot"
                :class="[$vClass('operators'), 'u-padding-hz-default']">
              <li
                v-for="(operator, index) in formatOperators(meetingSlot)"
                :class="['u-display-flex u-ai-center', index > 0 ? 'u-margin-top-xsmall' : '', operator.locked ? '--disabled' : '']"
                :set="(user = mappingMeetingOperator(operator.operator))"
              >
                <nxt-tasks-avatar :user="user" not-tooltip simple size="small"></nxt-tasks-avatar>
                <span class="u-margin-left-xsmall">
                  {{ user.first_name }} {{ user.last_name }}
                </span>
              </li>
            </ul>
          </template>
        </nxt-core-popover>
      </template>
    </appointment-booking>
    <template v-if="!meetingsSlotsLoading">
      <p v-if="fields.scheduled.loading"
         class="u-margin-vt-auto">
        {{ $t('meeting.refreshScheduled') }}
      </p>
      <nxt-core-tooltip
        :content="$t('base.tooltip.refreshGetMeetingSlot')"
        not-disabled-tooltip
        placement="top-start"
      >
        <nxt-tasks-btn
          :disabled="meetingsSlotsLoading"
          size="small"
          class="el-button--default button"
          @click="dateChange(datePickerDate)"
        >
          <font-awesome-icon :icon="['fas', 'redo']" class="fa-fw" />
        </nxt-tasks-btn>
      </nxt-core-tooltip>
      <nxt-core-tooltip
        v-if="displayBypassRules"
        :content="$t('base.tooltip.forceRefreshGetMeetingSlot')"
        not-disabled-tooltip
        placement="top-start"
      >
        <nxt-tasks-btn
          :disabled="meetingsSlotsLoading"
          circle
          class="el-button--default button"
          @click="dateChange(datePickerDate, true)"
        >
          <font-awesome-icon :icon="['fas', 'arrows-rotate']" class="fa-fw" />
        </nxt-tasks-btn>
      </nxt-core-tooltip>
    </template>
  </div>
</template>

<script>
import {mapState, mapActions, mapWritableState} from "pinia";
import { useMeetingStore } from "@nextories/vue-core-components/src/store/meetingStore.ts";
import Input from '@nextories/vue-core-components/src/components/atoms/input/NxtCoreInput.vue';
import { useProjectsStore } from "@nextories/vue-tasks/src/store/projectsStore.ts";
import { AppointmentBooking } from "@nextories/meeting-component";
import "@nextories/meeting-component/dist/lib/appointment-booking.css";
import { useUsersStore } from "@nextories/vue-tasks/src/store/usersStore.ts";
import {useAuthStore} from "@nextories/vue-tasks/src/store/authStore.ts";

export default {
  name: "Scheduled",
  nameClassStyle: "scheduled",
  extends: Input,
  emits: ["change", "update:modelValue", "changedValue", "update:duration"],
  components: {
    AppointmentBooking,
  },
  props: {
    modelValue: {
      type: Object,
    },
    operator: Object,
    fields: Object,
  },
  data() {
    return {
      datePickerDate: new Date(),
      disabledSlot: false,
    };
  },
  computed: {
    ...mapState(useMeetingStore, {
      durationStore: 'duration',
    }),
    ...mapState(useProjectsStore, ["project"]),
    ...mapWritableState(useMeetingStore, ["meetingsSlots", "locks", "lock"]),
    ...mapState(useMeetingStore, ["meetingsSlotsLoading", "savedApiResponse", "granularity"]),
    ...mapState(useUsersStore, ["users"]),
    ...mapState(useAuthStore, ['currentUser', 'multiplePusher']),
    isAdmin() {
      return this.$helpers.authorisation.isAdmin(this.currentUser);
    },
    // Force refresh calendar
    displayBypassRules() {
      return this.isAdmin
        && !(this.meetingsSlots && this.meetingsSlots.length)
        && (this.savedApiResponse.api_meeting
          && this.savedApiResponse.api_meeting.message === 'Query params `bypass_rules');
    },
    pusherN1() {
      return this.multiplePusher && this.multiplePusher.n1;
    },
  },
  async created() {
    try {
      if (this.pusherN1) {
        const channel = this.pusherN1.subscribe(import.meta.env.nxt.pusher.channel.locks);
        // Listen channel multiplePusher N1 create and delete lock
        this.subscribeGetLocks(channel);
        this.subscribeDeleteLocks(channel);
      }
      await this.dateChange(this.datePickerDate);
    } catch (error) {
      console.error(error);
    }
  },
  async beforeUnmount() {
    try {
      if (this.pusherN1) {
        this.pusherN1.unsubscribe(import.meta.env.nxt.pusher.channel.locks);
      }
      if (this.lock && this.lock.lockId) {
        await this.deleteLock(undefined, this.lock.lockId);
      }
    } catch (error) {
      console.error(error);
    }
  },
  methods: {
    ...mapActions(useMeetingStore, {postLockStore: "postLock", deleteLockStore: "deleteLock"}),
    ...mapActions(useMeetingStore, ["getMeetingsSlots", "setSavedApiResponse"]),
    subscribeGetLocks(channel) {
      channel.bind('lock_created', async (res) => {
        if (res && res.locks) {
          // Batch display slots
          this.locksSlots(res.locks, true);
        }
      });
    },
    subscribeDeleteLocks(channel) {
      channel.bind('locks_deleted', async (res) => {
        if (res && res.locks) {
          // Batch display slots
          this.locksSlots(res.locks, undefined);
        }
      });
    },
    // Param lock is true or false
    // If locked = true, it's post locked or created locked
    lockSlots(lock, locked) {
      for (const meetingSlots of this.meetingsSlots) {
        if (meetingSlots && meetingSlots.date === this.$helpers.calendar.formatDateStartOff(lock.slotStartTime)) {
          for (const slot of meetingSlots.slots) {
            if (slot && slot.slotStartTime === lock.slotStartTime) {
              if (slot.operators) {
                // Find index operator for set lock
                slot.operators = slot.operators.map((operator) => ({
                  ...operator,
                  locked: operator.operator === lock.calendarId ? locked : false,
                }));
                // Set lock when all operator occupied
                slot.locked = slot.operators.filter((operator) => operator.locked).length >= slot.operators.length;
              }
              // Not lock when the current model equal lock selected
              if (this.model && this.model.slotStartTime === lock.slotStartTime) {
                slot.locked = false;
                break;
              }
              break;
            }
          }
          break;
        }
      }
    },
    locksSlots(locks, locked) {
      for (const lock of locks) {
        if (lock && (lock.lockedBy && lock.lockedBy.id !== this.currentUser.id) || !lock.lockedBy) {
          this.lockSlots(lock, locked);
        }
      }
    },
    async meetingCheckAndUncheck(data, lock) {
      try {
        if (lock && data.uncheck) {
          await this.deleteLock(data.uncheck, null, !!data.check);
        }
        if (data.check) {
          await this.postLock(data.check);
        }
      } catch (error) {
        console.error(error);
      }
    },
    async postLock(slot) {
      try {
        this.disabledSlot = true;
        this.lockSlots(slot, true);
        const findOperator = this.getOperatorNotLocked(slot);
        if (findOperator) {
          const data = {
            operator: findOperator.operator,
            calendar_id: findOperator.calendarId,
            slot_start_time: slot.slotStartTime,
            duration: this.fields.duration.value,
            user_id: this.currentUser.id,
          };
          await this.postLockStore(data, this.$helpers.meeting.formatMeeting({
            ...data,
            ...slot,
            lockId: findOperator.lockId,
          }, findOperator.operator, this.users));
        }
      } catch (error) {
        this.$message({
          type: "error",
          message: error,
        });
        this.model = {};
        console.error(error);
      } finally {
        this.disabledSlot = false;
      }
    },
    async deleteLock(slot, operator, keepLoading) {
      try {
        this.disabledSlot = true;
        let findOperator;
        if (slot) {
          this.lockSlots(slot);
          findOperator = this.getOperatorNotLocked(slot);
        }
        await this.deleteLockStore(operator || (findOperator && findOperator.lockId), {
          duration: this.fields.duration.value,
        }, keepLoading);
      } catch (error) {
        this.$message({
          type: "error",
          message: error,
        });
        console.error(error);
      } finally {
        this.disabledSlot = false;
      }
    },
    async dateChange(date, bypassRules) {
      try {
        const operators = this.fields && this.fields.operators && this.fields.operators.value && this.fields.operators.value.length
          ? this.fields.operators.value : undefined;
        // Format get queries
        const queries = `granularity=${this.granularity}&language=${this.fields.language.value || 'FR'}${
          !!bypassRules ? `&bypass_rules=${!!bypassRules}` : ''}${
          operators ? `&operators=${operators}` : ''}${
          this.fields.meetingType.value ? `&visit_type=${this.fields.meetingType.value}` : ''}${
          this.project.meeting && this.project.meeting.date_start ? `&old_date=${this.project.meeting.date_start}` : ''}&user_id=${this.currentUser.id}`;
        if (this.lock && this.lock.lockId) {
          await this.deleteLock(undefined, this.lock.lockId);
        }
        await this.getMeetingsSlots(
          7,
          this.fields.duration.value || this.durationStore,
          this.$helpers.format.formatDateWithTimezone(date),
          queries,
        );
        this.formatMeetingsSlots();
        this.model = {};
        this.setSavedApiResponse({
          api_meeting: {
            ...this.savedApiResponse.api_meeting,
            operators,
            language: this.fields.language.value || 'FR',
          },
        });
        if (this.savedApiResponse.api_meeting.duration) {
          await this.$emit('update:duration', this.savedApiResponse.api_meeting.duration);
        }
        this.fields.scheduled.loading = false;
      } catch (error) {
        this.$message({
          type: "error",
          message: this.$t("base.messageError.slotsAvailable"),
        });
      }
    },
    getOperatorNotLocked(slot) {
      return slot.operators && slot.operators.find((operator) => operator && !operator.locked);
    },
    formatLockId(calendar, date) {
      return `${calendar}-${date}`;
    },
    // Format meetings slot
    formatMeetingsSlots() {
      for (const meeting of this.meetingsSlots) {
        for (const slot of meeting.slots) {
          // Set lock when all operator occupied
          slot.locked = slot.operators.filter((operator) => operator.locked).length >= slot.operators.length;
          slot.slotStartTime = Date.parse(slot.start);
          slot.operators = slot.operators.map((operator) => ({
            ...operator,
            lockId: this.formatLockId(operator.operator, slot.slotStartTime),
            // Set calendarId
            calendarId: operator.operator,
          }));
        }
      }
    },
    formatOperators(meetingSlot) {
      return this.$helpers.sorts.sortByNumber("score", meetingSlot.operators, true);
    },
    mappingMeetingOperator(operator) {
      return this.$helpers.meeting.mappingMeetingOperator(operator, this.users);
    },
  },
  watch: {
    'fields.scheduled.loading': async function (value) {
      try {
        if (value) {
          this.datePickerDate = new Date();
          if (this.lock && this.lock.lockId) {
            await this.deleteLock(undefined, this.lock.lockId);
          }
        }
      } catch (error) {
        console.error(error);
      }
    }
  },
};
</script>
<style lang="scss" scoped>
.#{$prefixName}scheduled {
  max-width: 670px;

  &__operators {
    li.--disabled {
      color: var(--nxt-color-ghost);

      :deep(.el-avatar) {
        filter: grayscale(1);
      }
    }
  }

  .appointment-booking {
    :deep(.tab) {
      min-height: 240px;

      @include breakpoint(tablet) {
        min-height: 260px;
      }
    }
  }

  :deep(.meeting) {
    position: relative;

    .el-tooltip__trigger {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      &:focus {
        outline: none;
      }
    }
  }
}
</style>
